// user-client/src/App.js
import React, { lazy, useEffect, useMemo, useState } from 'react';
import AppRouter from './routes';
import { readUiOverride, detectUiAuto, readViewportSize, isRealMobileDevice } from './shared/utils/uiMode';
import MatchQueueWaitOverlay from './shared/components/MatchQueueWaitOverlay';
import OperationsAnnouncementLayer from './shared/operations/OperationsAnnouncementLayer';
import MaintenanceSwapReloadBridge from './shared/maintenance/MaintenanceSwapReloadBridge';
import { AuthStateProvider } from './auth/AuthStateContext';
import { installUiSizeRuntime } from './shared/uiScale/uiSizeRuntime';
import { hasBrowserAuthSession, hasFreshLiveAuthWindow, installAuthWindowLease, isAutoLoginEnabled } from './shared/utils/authPersistence';

// Heavy lobby/table runtimes are route-loaded only when needed. Global safety/notice
// bridges stay synchronous so their event subscriptions behave exactly as before.
const CoreRoot = lazy(() => import('./core/Root'));
const MobileMultiTableRoot = lazy(() => import('./mobile/MobileMultiTableRoot'));
const DesktopMultiTableRoot = lazy(() => import('./desktop/DesktopMultiTableRoot'));

function AuthWindowLeaseBootstrap() {
  useEffect(() => {
    try {
      const shouldBootstrap = isAutoLoginEnabled() || hasBrowserAuthSession() || hasFreshLiveAuthWindow();
      if (!shouldBootstrap) return undefined;
      return installAuthWindowLease();
    } catch (_) {
      return undefined;
    }
  }, []);
  return null;
}

function isStandaloneTableRoute() {
  try {
    const p = String(window.location?.pathname || '');
    const search = new URLSearchParams(String(window.location?.search || ''));
    const popupTableId = String(search.get('popupTable') || '').trim();

    return (
      Boolean(popupTableId) ||
      p === '/webtoon-popup' ||
      p === '/popup' ||
      p.startsWith('/popup/') ||
      p.startsWith('/table/')
    );
  } catch (_) {
    return false;
  }
}

function applyGlobalAssetPatch(detail = {}) {
  try {
    const asset = detail?.asset ?? detail?.Asset ?? detail?.asset_balance_after ?? detail?.asset_after ?? detail?.userAssetAfter ?? detail?.user_asset_after ?? detail?.balance_after;
    const rakeback = detail?.rakeback_balance ?? detail?.Rakeback_balance;
    const loungePoint = detail?.lounge_point_balance ?? detail?.Lounge_point_balance;
    const patch = { ...(detail && typeof detail === 'object' ? detail : {}) };
    if (asset !== undefined && asset !== null) {
      patch.asset = asset;
      patch.Asset = asset;
    }
    if (rakeback !== undefined && rakeback !== null) {
      patch.rakeback_balance = rakeback;
      patch.Rakeback_balance = rakeback;
    }
    if (loungePoint !== undefined && loungePoint !== null) {
      patch.lounge_point_balance = loungePoint;
      patch.Lounge_point_balance = loungePoint;
    }
    if (patch.asset === undefined && patch.rakeback_balance === undefined && patch.lounge_point_balance === undefined) return false;

    try {
      const raw = localStorage.getItem('user');
      const prev = raw ? JSON.parse(raw) : {};
      localStorage.setItem('user', JSON.stringify({ ...(prev || {}), ...patch }));
    } catch (_) {}
    try { window.dispatchEvent(new CustomEvent('user:asset-updated', { detail: patch })); } catch (_) {}
    return true;
  } catch (_) {
    return false;
  }
}

function readUrlRuntimeMode() {
  try {
    const p = new URLSearchParams(String(window.location?.search || ''));
    const q = String(p.get('ui') || '').trim().toLowerCase();

    // [FIX][2026-08-30][ANCHOR:APP:REAL_MOBILE_RUNTIME_ROOT]
    // 명시 URL override는 최우선으로 유지한다.
    // - ?ui=mobile  -> MobileMultiTableRoot
    // - ?ui=desktop -> DesktopMultiTableRoot
    // 일반 URL에서는 실제 스마트폰/태블릿만 MobileMultiTableRoot를 사용한다.
    // 따라서 PC 창 폭만 줄어든 compact mobile UI는 기존처럼 desktop runtime을 유지한다.
    if (q === 'mobile') return 'mobile';
    if (q === 'desktop') return 'desktop';
    return isRealMobileDevice() ? 'mobile' : 'desktop';
  } catch (_) {
    return 'desktop';
  }
}

function detectMode() {
  const allowCompactViewportMobile = !isStandaloneTableRoute();

  // 화면 표시 모드와 runtime Root를 분리한다.
  // - ?ui=mobile: 모바일 전용 URL이므로 mobile UI 고정
  // - ?ui=desktop / 일반 데스크톱 URL: viewport 폭에 따라 desktop <-> mobile UI 전환
  //   단, runtime Root는 readUrlRuntimeMode()에서 desktop으로 유지된다.
  const ov = readUiOverride(); // 'mobile' | 'desktop' | null
  if (ov === 'mobile') return 'mobile';

  // desktop override는 더 이상 "desktop UI 고정" 의미가 아니다.
  // 데스크톱 URL에서도 가로 폭이 좁아지면 mobile lobby UI를 사용해야 한다.
  return detectUiAuto({ allowCompactViewportMobile }); // 'mobile' | 'desktop'
}

export default function App() {
  const [mode, setMode] = useState(() => detectMode());
  const [runtimeMode, setRuntimeMode] = useState(() => readUrlRuntimeMode());

  useEffect(() => installUiSizeRuntime(), []);

  // [ADD][ANCHOR:APP:ASSET_UPDATED_CROSS_WINDOW_BRIDGE]
  // 데스크톱 테이블 팝업(/game socket)에서 전달한 자산 갱신을 메인 로비창에서도 즉시 반영한다.
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        if (event?.origin && event.origin !== window.location.origin) return;
        const data = event?.data || {};
        const type = String(data?.type || data?.event || '').trim();
        if (type !== 'USER_ASSET_UPDATED' && type !== 'user:asset-updated' && type !== 'userAssetUpdated') return;
        applyGlobalAssetPatch(data?.detail || data?.payload || data);
      } catch (_) {}
    };

    let bc = null;
    try {
      if ('BroadcastChannel' in window) {
        bc = new BroadcastChannel('user-asset-updates');
        bc.onmessage = (event) => {
          const data = event?.data || {};
          const type = String(data?.type || data?.event || '').trim();
          if (type === 'USER_ASSET_UPDATED' || type === 'user:asset-updated' || type === 'userAssetUpdated') {
            applyGlobalAssetPatch(data?.detail || data?.payload || data);
          }
        };
      }
    } catch (_) {
      bc = null;
    }

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      try { bc?.close?.(); } catch (_) {}
    };
  }, []);

  useEffect(() => {
    // UI 표시 모드는 viewport에 따라 바뀔 수 있지만 runtime Root는
    // 명시 URL override 또는 실제 모바일 기기 여부로 결정한다.
    // PC의 compact viewport 전환은 lobbyVariant만 mobile로 바뀌며 desktop runtime은 유지된다.
    let raf = null;

    const applyMode = () => {
      const urlRuntimeMode = readUrlRuntimeMode();
      setRuntimeMode((prev) => (prev === urlRuntimeMode ? prev : urlRuntimeMode));

      // 모바일 전용 URL은 폭과 관계없이 mobile UI/runtime을 유지한다.
      if (urlRuntimeMode === 'mobile') {
        setMode((prev) => (prev === 'mobile' ? prev : 'mobile'));
        return;
      }

      // 실제 모바일 기기는 일반 URL에서도 mobile runtime/UI를 함께 사용한다.
      // (명시 ?ui=desktop은 위 readUrlRuntimeMode()에서 desktop으로 유지된다.)
      if (isRealMobileDevice()) {
        setMode((prev) => (prev === 'mobile' ? prev : 'mobile'));
        return;
      }

      // 데스크톱 창을 960px 경계에서 왕복할 때 UI가 연속 변경되지 않도록
      // mobile -> desktop 복귀 경계에만 60px hysteresis를 둔다.
      const { w } = readViewportSize();
      setMode((prev) => {
        if (prev === 'mobile' && w < 1020) return prev;
        const next = detectMode();
        return prev === next ? prev : next;
      });
    };

    const scheduleApply = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyMode);
    };

    const onPop = () => scheduleApply();
    const onResize = () => {
      if (document.documentElement?.dataset?.npcEventModalViewportFreeze === '1') return;
      if (isStandaloneTableRoute()) return;
      scheduleApply();
    };

    window.addEventListener('popstate', onPop);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }

    scheduleApply();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, []);



  const Root = useMemo(() => {
    // Root/runtime은 명시 URL override 또는 실제 모바일 기기 여부로 고정한다.
    // 일반 PC에서는 창 폭을 줄여도 lobbyVariant만 mobile로 바뀌므로
    // DesktopMultiTableRoot / DesktopScreenController가 unmount되지 않는다.
    const useMobileRuntimeRoot = runtimeMode === 'mobile';
    const Impl = useMobileRuntimeRoot ? MobileMultiTableRoot : DesktopMultiTableRoot;
    const runtimePlatform = useMobileRuntimeRoot ? 'mobile' : 'desktop';

    return function RootWrapper({
      lobbyVariant = useMobileRuntimeRoot ? 'mobile' : 'desktop',
      initialViewKey = 'home',
      initialLoungePostCode = '',
      initialLoungeVipGuideOpen = false,
    } = {}) {
      return (
        <CoreRoot
          Impl={Impl}
          runtimePlatform={runtimePlatform}
          lobbyVariant={lobbyVariant}
          initialViewKey={initialViewKey}
          initialLoungePostCode={initialLoungePostCode}
          initialLoungeVipGuideOpen={initialLoungeVipGuideOpen}
        />
      );
    };
  }, [runtimeMode]);


  return (
    <AuthStateProvider>
      <AuthWindowLeaseBootstrap />
      <MaintenanceSwapReloadBridge />
      <AppRouter
        Root={Root}
        mode={mode}
      />
      {!isStandaloneTableRoute() ? <MatchQueueWaitOverlay /> : null}
      <OperationsAnnouncementLayer />
    </AuthStateProvider>
  );
}
